import React, { useState } from 'react';
import {
  Card,
  Title,
  Text,
  Group,
  Badge,
  Timeline,
  Avatar,
  Stack,
  Button,
  Select,
  ActionIcon,
  Tooltip,
  Collapse,
  Divider,
  Progress,
  Paper,
  ThemeIcon,
  Modal,
  Grid,
  Tabs,
} from '@mantine/core';
import {
  IconActivity,
  IconCalendar,
  IconClock,
  IconMapPin,
  IconCamera,
  IconUser,
  IconEye,
  IconFilter,
  IconChevronDown,
  IconChevronUp,
  IconDownload,
  IconShare,
  IconAlertTriangle,
  IconPhoto,
  IconVideo,
  IconNotes,
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { useDetections } from '../../hooks/useDetections';
import { useParentActivityTimeline } from '../../hooks/parent/useParentActivityTimeline';
import { useAuth } from '../../contexts/AuthContext';
import type { Detection, Student } from '../../types/api';

interface ActivityTimelineProps {
  studentId?: string;
  student?: Student;
  maxItems?: number;
  showFilters?: boolean;
  showHeader?: boolean;
  dateRange?: {
    start: Date;
    end: Date;
  };
}

const ActivityTimeline: React.FC<ActivityTimelineProps> = ({
  studentId,
  student,
  maxItems = 10,
  showFilters = true,
  showHeader = true,
  dateRange,
}) => {
  const [activityTypeFilter, setActivityTypeFilter] = useState<string>('');
  const [dateFilter, setDateFilter] = useState<[Date | null, Date | null]>([null, null]);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [selectedActivity, setSelectedActivity] = useState<Detection | null>(null);

  const [detailModalOpened, { open: openDetailModal, close: closeDetailModal }] = useDisclosure(false);
  const { user } = useAuth();

  // Use different data sources based on user role
  const isParentUser = user?.role === 'viewer' || user?.role === 'parent';

  // For parent users, use parent-specific activity timeline hook
  const parentTimelineResult = useParentActivityTimeline({
    studentId: isParentUser ? studentId : undefined,
    maxItems,
    dateRange,
    activityTypeFilter,
    dateFilter,
  });

  // For admin/teacher users, use detections hook
  const adminDetectionsResult = useDetections(isParentUser ? undefined : {
    student_id: studentId?.toString(),
    per_page: maxItems,
    sort_by: 'timestamp',
    sort_order: 'desc',
    filters: {
      ...(activityTypeFilter && { activity_type: activityTypeFilter }),
      ...(dateFilter[0] && { start_date: dateFilter[0].toISOString().split('T')[0] }),
      ...(dateFilter[1] && { end_date: dateFilter[1].toISOString().split('T')[0] }),
      ...(dateRange && {
        start_date: dateRange.start.toISOString().split('T')[0],
        end_date: dateRange.end.toISOString().split('T')[0]
      }),
    },
  });

  // Determine which data to use
  const isLoading = isParentUser ? parentTimelineResult.isLoading : adminDetectionsResult.isLoading;
  const error = isParentUser ? parentTimelineResult.error : adminDetectionsResult.error;

  const detections: any[] = isParentUser
    ? parentTimelineResult.detections
    : (Array.isArray(adminDetectionsResult.data) ? adminDetectionsResult.data : (adminDetectionsResult.data && adminDetectionsResult.data.detections) || []);

  const toggleExpanded = (detectionId: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(detectionId)) {
      newExpanded.delete(detectionId);
    } else {
      newExpanded.add(detectionId);
    }
    setExpandedItems(newExpanded);
  };

  const handleViewActivity = (detection: Detection) => {
    setSelectedActivity(detection);
    openDetailModal();
  };

  const getActivityTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      'entry': 'green',
      'exit': 'blue',
      'play': 'violet',
      'sleep': 'indigo',
      'meal': 'orange',
      'safety_concern': 'red',
      'incident': 'red',
      'unknown': 'gray',
    };
    return colors[type] || 'gray';
  };

  const getActivityIcon = (type: string) => {
    const icons: Record<string, React.ReactNode> = {
      'entry': <IconUser size={12} />,
      'exit': <IconUser size={12} />,
      'play': <IconActivity size={12} />,
      'sleep': <IconClock size={12} />,
      'meal': <IconActivity size={12} />,
      'safety_concern': <IconAlertTriangle size={12} />,
      'incident': <IconAlertTriangle size={12} />,
      'unknown': <IconActivity size={12} />,
    };
    return icons[type] || <IconActivity size={12} />;
  };

  const formatActivityTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const exportActivities = () => {
    notifications.show({
      title: 'Export Started',
      message: 'Activity report is being generated...',
      color: 'blue',
    });
  };

  if (error) {
    return (
      <Card withBorder>
        <Text c="red" ta="center">Failed to load activities</Text>
      </Card>
    );
  }

  return (
    <>
      <Card withBorder>
        {showHeader && (
          <Group justify="space-between" mb="md">
            <div>
              <Title order={4}>
                {student ? `${student.first_name}'s Activity Timeline` : 'Activity Timeline'}
              </Title>
              <Text size="sm" c="dimmed">
                Recent activities and detections
              </Text>
            </div>
            <Group>
              <Button
                variant="outline"
                size="xs"
                leftSection={<IconDownload size={14} />}
                onClick={exportActivities}
              >
                Export
              </Button>
              <Button
                variant="outline"
                size="xs"
                leftSection={<IconShare size={14} />}
              >
                Share
              </Button>
            </Group>
          </Group>
        )}

        {/* Filters */}
        {showFilters && (
          <Stack gap="md" mb="md">
            <Group>
              <Select
                placeholder="Activity type"
                leftSection={<IconFilter size={16} />}
                data={[
                  { value: '', label: 'All Activities' },
                  { value: 'entry', label: 'Entry' },
                  { value: 'exit', label: 'Exit' },
                  { value: 'play', label: 'Play' },
                  { value: 'sleep', label: 'Sleep' },
                  { value: 'meal', label: 'Meal' },
                  { value: 'safety_concern', label: 'Safety Concern' },
                  { value: 'incident', label: 'Incident' },
                ]}
                value={activityTypeFilter}
                onChange={(value) => setActivityTypeFilter(value || '')}
                style={{ minWidth: 150 }}
              />
              <DatePickerInput
                type="range"
                placeholder="Pick date range"
                leftSection={<IconCalendar size={16} />}
                value={dateFilter}
                onChange={setDateFilter}
                style={{ minWidth: 200 }}
              />
            </Group>
          </Stack>
        )}

        {/* Activity Timeline */}
        {isLoading ? (
          <Stack gap="md">
            {[...Array(3)].map((_, i) => (
              <div key={i} style={{ height: '60px', background: 'var(--mantine-color-gray-1)', borderRadius: '4px' }} />
            ))}
          </Stack>
        ) : detections.length === 0 ? (
          <Paper p="xl" style={{ textAlign: 'center', backgroundColor: 'var(--mantine-color-gray-0)' }}>
            <ThemeIcon size="xl" variant="light" color="gray" mx="auto" mb="md">
              <IconActivity size={24} />
            </ThemeIcon>
            <Text fw={500}>No activities found</Text>
            <Text size="sm" c="dimmed">
              {activityTypeFilter || dateFilter[0] || dateFilter[1]
                ? 'Try adjusting your filters'
                : 'Activities will appear here as they occur'}
            </Text>
          </Paper>
        ) : (
          <Timeline active={detections.length} color="blue" lineWidth={2}>
            {detections.map((detection: any, index: number) => {
              const isExpanded = expandedItems.has(detection.id);
              const isLast = index === detections.length - 1;

              return (
                <Timeline.Item
                  key={detection.id}
                  bullet={
                    <ThemeIcon
                      size={24}
                      radius="xl"
                      color={getActivityTypeColor(detection.activity_type)}
                      variant="light"
                    >
                      {getActivityIcon(detection.activity_type)}
                    </ThemeIcon>
                  }
                  title={
                    <Group justify="space-between" w="100%">
                      <Group gap="xs">
                        <Text fw={500} size="sm">
                          {detection.activity_type?.replace('_', ' ')}
                        </Text>
                        <Badge
                          color={getActivityTypeColor(detection.activity_type)}
                          variant="light"
                          size="xs"
                        >
                          {((detection.confidence || 0) * 100).toFixed(0)}%
                        </Badge>
                        {detection.safety_score && (
                          <Badge
                            color={detection.safety_score > 70 ? 'green' : detection.safety_score > 40 ? 'orange' : 'red'}
                            variant="dot"
                            size="xs"
                          >
                            Safety: {detection.safety_score}
                          </Badge>
                        )}
                      </Group>
                      <Group gap={4}>
                        <Tooltip label="View details">
                          <ActionIcon
                            size="xs"
                            variant="subtle"
                            onClick={() => handleViewActivity(detection)}
                          >
                            <IconEye size={12} />
                          </ActionIcon>
                        </Tooltip>
                        <ActionIcon
                          size="xs"
                          variant="subtle"
                          onClick={() => toggleExpanded(detection.id)}
                        >
                          {isExpanded ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />}
                        </ActionIcon>
                      </Group>
                    </Group>
                  }
                >
                  <Text size="xs" c="dimmed" mb={4}>
                    {formatActivityTime(detection.timestamp)}
                  </Text>

                  <Group gap="xs" mb="xs">
                    <Group gap={2}>
                      <IconMapPin size={10} />
                      <Text size="xs">{detection.camera?.location?.area?.name || 'Unknown Area'}</Text>
                    </Group>
                    <Text size="xs" c="dimmed">•</Text>
                    <Group gap={2}>
                      <IconCamera size={10} />
                      <Text size="xs">{detection.camera?.name || 'Unknown Camera'}</Text>
                    </Group>
                  </Group>

                  {!student && detection.student && (
                    <Group gap="xs" mb="xs">
                      <Avatar size="xs" radius="xl">
                        {detection.student.first_name[0] + detection.student.last_name[0]}
                      </Avatar>
                      <Text size="xs">
                        {detection.student.first_name} {detection.student.last_name}
                      </Text>
                    </Group>
                  )}

                  <Collapse in={isExpanded}>
                    <Stack gap="xs" mt="xs" p="xs" style={{ background: 'var(--mantine-color-gray-0)', borderRadius: '4px' }}>
                      <Group justify="space-between">
                        <Text size="xs" c="dimmed">Confidence:</Text>
                        <Group gap="xs">
                          <Progress
                            value={(detection.confidence || 0) * 100}
                            size="xs"
                            style={{ width: 60 }}
                            color={detection.confidence > 0.8 ? 'green' : detection.confidence > 0.6 ? 'yellow' : 'red'}
                          />
                          <Text size="xs" fw={500}>
                            {((detection.confidence || 0) * 100).toFixed(1)}%
                          </Text>
                        </Group>
                      </Group>

                      <Group justify="space-between">
                        <Text size="xs" c="dimmed">Verification:</Text>
                        <Badge
                          color={
                            detection.verification_status === 'verified' ? 'green' :
                            detection.verification_status === 'rejected' ? 'red' :
                            detection.verification_status === 'needs_review' ? 'orange' : 'blue'
                          }
                          variant="light"
                          size="xs"
                        >
                          {detection.verification_status?.replace('_', ' ')}
                        </Badge>
                      </Group>

                      {detection.verification_notes && (
                        <div>
                          <Text size="xs" c="dimmed" mb={2}>Notes:</Text>
                          <Text size="xs" style={{ fontStyle: 'italic' }}>
                            {detection.verification_notes}
                          </Text>
                        </div>
                      )}

                      <Group>
                        <Button size="xs" variant="subtle" leftSection={<IconPhoto size={10} />}>
                          View Image
                        </Button>
                        <Button size="xs" variant="subtle" leftSection={<IconVideo size={10} />}>
                          Watch Video
                        </Button>
                        <Button size="xs" variant="subtle" leftSection={<IconNotes size={10} />}>
                          Add Note
                        </Button>
                      </Group>
                    </Stack>
                  </Collapse>

                  {!isLast && <Divider my="xs" />}
                </Timeline.Item>
              );
            })}
          </Timeline>
        )}

        {/* Load More */}
        {detections.length >= maxItems && (
          <Group justify="center" mt="md">
            <Button variant="outline" size="sm">
              Load More Activities
            </Button>
          </Group>
        )}
      </Card>

      {/* Activity Detail Modal */}
      <Modal
        opened={detailModalOpened}
        onClose={closeDetailModal}
        title="Activity Details"
        size="lg"
      >
        {selectedActivity && (
          <Stack gap="md">
            <Group justify="space-between">
              <div>
                <Title order={4}>
                  {selectedActivity.activity_type?.replace('_', ' ')}
                </Title>
                <Text size="sm" c="dimmed">
                  {selectedActivity.timestamp ? new Date(selectedActivity.timestamp).toLocaleString() : 'No timestamp'}
                </Text>
              </div>
              <Badge
                color={getActivityTypeColor(selectedActivity.activity_type || 'unknown')}
                size="lg"
              >
                {selectedActivity.activity_type?.replace('_', ' ')}
              </Badge>
            </Group>

            <Tabs defaultValue="overview">
              <Tabs.List>
                <Tabs.Tab value="overview">Overview</Tabs.Tab>
                <Tabs.Tab value="evidence">Evidence</Tabs.Tab>
                <Tabs.Tab value="context">Context</Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="overview" pt="md">
                <Grid>
                  <Grid.Col span={6}>
                    <Stack gap="xs">
                      <Group justify="space-between">
                        <Text size="sm">Student:</Text>
                        <Text size="sm" fw={500}>
                          {selectedActivity.student ?
                            `${selectedActivity.student.first_name} ${selectedActivity.student.last_name}` :
                            'Unknown'
                          }
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm">Location:</Text>
                        <Text size="sm" fw={500}>
                          {selectedActivity.camera?.location?.area?.name || 'Unknown'}
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm">Camera:</Text>
                        <Text size="sm" fw={500}>
                          {selectedActivity.camera?.name || 'Unknown'}
                        </Text>
                      </Group>
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Stack gap="xs">
                      <Group justify="space-between">
                        <Text size="sm">Confidence:</Text>
                        <Text size="sm" fw={500}>
                          {((selectedActivity.confidence || 0) * 100).toFixed(1)}%
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm">Safety Score:</Text>
                        <Text size="sm" fw={500}>
                          {selectedActivity.safety_score || 'N/A'}
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm">Status:</Text>
                        <Badge
                          color={
                            selectedActivity.verification_status === 'verified' ? 'green' :
                            selectedActivity.verification_status === 'false_positive' ? 'red' :
                            selectedActivity.verification_status === 'needs_review' ? 'orange' : 'blue'
                          }
                          variant="light"
                          size="sm"
                        >
                          {selectedActivity.verification_status?.replace('_', ' ')}
                        </Badge>
                      </Group>
                    </Stack>
                  </Grid.Col>
                </Grid>
              </Tabs.Panel>

              <Tabs.Panel value="evidence" pt="md">
                <Paper withBorder p="xl" style={{ textAlign: 'center' }}>
                  <IconCamera size={48} color="gray" />
                  <Text c="dimmed" mt="md">
                    Detection evidence (image/video) would appear here
                  </Text>
                </Paper>
              </Tabs.Panel>

              <Tabs.Panel value="context" pt="md">
                <Stack gap="md">
                  <div>
                    <Text size="sm" fw={500} mb="xs">Timeline Context</Text>
                    <Text size="xs" c="dimmed">
                      • Previous activity: Entry at 9:15 AM
                    </Text>
                    <Text size="xs" c="dimmed">
                      • Duration in area: 45 minutes
                    </Text>
                    <Text size="xs" c="dimmed">
                      • Next activity: Meal at 12:00 PM
                    </Text>
                  </div>

                  {selectedActivity.verification_notes && (
                    <div>
                      <Text size="sm" fw={500} mb="xs">Verification Notes</Text>
                      <Text size="sm" style={{ fontStyle: 'italic' }}>
                        {selectedActivity.verification_notes}
                      </Text>
                    </div>
                  )}
                </Stack>
              </Tabs.Panel>
            </Tabs>
          </Stack>
        )}
      </Modal>
    </>
  );
};

export default ActivityTimeline;
